import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Dropdown, Input, Message, Segment, Table } from 'semantic-ui-react';

import InfluencerRow from './influencer-row';
import SkeletonRow from './skeleton-row';
import Pager from './pager';

const hcStyle = { textTransform: 'uppercase', letterSpacing: '0.04em' };
// Wide enough for the full placeholder, and it keeps that width as the segment flexes.
const searchStyle = { minWidth: 380, flex: '0 0 380px' };
const countStyle = { color: '#9090a0' };
const filterStyle = { marginLeft: 12, minWidth: 150 };
const countCellStyle = { ...hcStyle, width: 80 };

// Semantic renders an empty box for value='', so "all" needs a real value.
const POSTED_ALL = 'all';
const POSTED_OPTIONS = [
  { key: 'all', value: POSTED_ALL, text: 'All creators' },
  { key: 'yes', value: 'true', text: 'Posted' },
  { key: 'no', value: 'false', text: 'Not posted' },
];

const SearchBox = ({ value, onChange, onClear }) => (
  <Input
    icon={value ? { name: 'close', link: true, onClick: onClear } : 'search'}
    iconPosition={value ? undefined : 'left'}
    placeholder="Search instagram or tiktok handle…"
    value={value}
    onChange={onChange}
    fluid
    style={searchStyle}
  />
);

SearchBox.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

const InfluencersTab = ({
  creators, page, pageSize, total, isLoading, hasFailed, onPageChange,
  sort, onSortChange, searchInput, searchTerm, onSearchChange, onSearchClear, onCreatorSelect,
  posted, onPostedChange,
}) => {
  const scannedSorted = sort === 'desc' ? 'descending' : 'ascending';
  const isLastPage = total != null ? (page + 1) * pageSize >= total : creators.length < pageSize;
  const isEmpty = !isLoading && !hasFailed && !creators.length;

  // Derive the range from the rows actually returned; page arithmetic alone reads
  // "Showing 201–6 of 6" when a URL points past the end of the results.
  const shown = creators.length;
  const rangeStart = (page * pageSize) + 1;
  const isPastEnd = !isLoading && !shown && total > 0;

  let countLabel = '';
  if (shown) {
    const suffix = total != null ? ` of ${total}` : '';
    countLabel = `Showing ${rangeStart}–${(rangeStart + shown) - 1}${suffix} influencers`;
  } else if (isPastEnd) {
    countLabel = `${total} influencers`;
  } else if (!isLoading && total === 0) {
    countLabel = 'No influencers';
  }

  let emptyMessage = 'No influencers on this campaign yet.';
  if (isPastEnd) emptyMessage = 'This page is past the end of the results — go back to the first page.';
  else if (searchTerm) emptyMessage = `No influencers matching "${searchTerm}".`;
  else if (posted === 'true') emptyMessage = 'No influencers have posted yet.';
  else if (posted === 'false') emptyMessage = 'Every influencer on this campaign has posted.';

  return (
    <Segment.Group>
      <Segment secondary style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SearchBox value={searchInput} onChange={onSearchChange} onClear={onSearchClear} />
        <Dropdown
          selection
          compact
          style={filterStyle}
          options={POSTED_OPTIONS}
          value={posted || POSTED_ALL}
          onChange={onPostedChange}
        />
        <span style={countStyle}>{countLabel}</span>
        <Pager page={page} isLastPage={isLastPage} disabled={isLoading} onChange={onPageChange} />
      </Segment>

      {hasFailed && <Segment><Message error content="Could not load influencers. Please try again." /></Segment>}
      {isEmpty && <Segment><Message info content={emptyMessage} /></Segment>}

      {!hasFailed && !isEmpty &&
        <Segment>
          <Table sortable basic="very" selectable unstackable style={{ tableLayout: 'fixed' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ ...hcStyle, width: '45%' }}>Influencer</Table.HeaderCell>
                <Table.HeaderCell style={{ ...hcStyle, width: 200 }}>Platforms</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={countCellStyle}>Posted</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={countCellStyle}>Stories</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={countCellStyle}>Posts</Table.HeaderCell>
                <Table.HeaderCell
                  sorted={scannedSorted}
                  onClick={onSortChange}
                  style={{ ...hcStyle, width: 150 }}
                >
                  Last Scanned
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading
                ? _.times(pageSize, i => <SkeletonRow key={i} />)
                : creators.map(creator => (
                  <InfluencerRow key={creator.id} creator={creator} onSelect={onCreatorSelect} />
                ))}
            </Table.Body>
          </Table>
        </Segment>}

      <Segment secondary textAlign="center">
        <Pager page={page} isLastPage={isLastPage} disabled={isLoading} onChange={onPageChange} />
      </Segment>
    </Segment.Group>
  );
};

InfluencersTab.propTypes = {
  creators: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  hasFailed: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  searchInput: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchClear: PropTypes.func.isRequired,
  onCreatorSelect: PropTypes.func.isRequired,
  posted: PropTypes.string.isRequired,
  onPostedChange: PropTypes.func.isRequired,
};

InfluencersTab.defaultProps = {
  total: null,
};

export default InfluencersTab;



/** WEBPACK FOOTER **
 ** ./src/containers/large-campaign/influencers-tab.js
 **/